// Pembaruan versi aplikasi (deploy baru) lewat service worker mode "prompt".
//
// SW baru dipasang di background lalu menunggu (waiting) sampai diizinkan aktif,
// jadi tab yang sedang dibuka tetap memakai aset versi lama dari cache SW lama.
// Halaman tidak pernah di-reload paksa saat sedang dibuka. Versi baru dipasang saat:
// - user klik "Muat ulang" di notifikasi (UpdateAppWrapper), atau
// - user pindah halaman (useAppUpdate), selama kolom chat tidak berisi draft.

const SW_URL = "/sw.js";
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000;
// Jeda minimal pengecekan saat tab kembali aktif
const VISIBLE_CHECK_THROTTLE = 60 * 1000;
// Kalau SW baru tidak kunjung mengambil alih, reload tetap jalan supaya tombol tidak macet
const ACTIVATE_TIMEOUT = 3000;

// Kata kunci path halaman form. Muat ulang manual di sini minta konfirmasi dulu
// karena isian yang belum disimpan akan hilang.
const UNSAFE_PATH_KEYWORDS = [
  "/add",
  "/edit",
  "/create",
  "/import",
  "/billing/upgrade",
  "/billing/addon",
  "/settings/company",
  "/settings/profile",
];

let state = { updateAvailable: false, hasChatDraft: false };
const listeners = new Set();

let registration = null;
let lastVisibleCheck = 0;
let applying = false;
let reloading = false;

const setState = (patch) => {
  const next = { ...state, ...patch };
  if (
    next.updateAvailable === state.updateAvailable &&
    next.hasChatDraft === state.hasChatDraft
  ) {
    return;
  }
  state = next;
  listeners.forEach((listener) => listener());
};

export const subscribeAppUpdate = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getAppUpdateState = () => state;

// Dipanggil kolom chat (SendMessage) saat isi pesan berubah
export const setChatDraft = (hasChatDraft) => {
  setState({ hasChatDraft: Boolean(hasChatDraft) });
};

export const isSafeToReloadPath = (pathname = "") =>
  !UNSAFE_PATH_KEYWORDS.some((keyword) => pathname.includes(keyword));

const reloadPage = () => {
  if (reloading) return;
  reloading = true;
  window.location.reload();
};

export const applyAppUpdate = () => {
  if (applying) return;
  applying = true;

  // SW baru sudah aktif (diizinkan dari tab lain) → cukup reload
  const waitingSW = registration?.waiting;
  if (!waitingSW) {
    reloadPage();
    return;
  }

  // Reload dijalankan listener `controllerchange` setelah SW baru mengambil alih
  waitingSW.postMessage({ type: "SKIP_WAITING" });
  setTimeout(reloadPage, ACTIVATE_TIMEOUT);
};

const markUpdateAvailable = () => {
  setState({ updateAvailable: true });
};

const watchInstallingSW = (worker) => {
  worker?.addEventListener("statechange", () => {
    // Tanpa controller berarti instalasi pertama, bukan update
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      markUpdateAvailable();
    }
  });
};

const checkForUpdate = () => {
  registration?.update().catch(() => {
    // offline / sw.js gagal dimuat — pengecekan berikutnya akan mencoba lagi
  });
};

// Interval timer melambat saat tab di background, jadi cek langsung saat tab kembali aktif.
// `focus` menangkap kembali dari window lain, `visibilitychange` dari tab lain.
const checkOnVisible = () => {
  const now = Date.now();
  if (
    document.visibilityState !== "visible" ||
    now - lastVisibleCheck < VISIBLE_CHECK_THROTTLE
  ) {
    return;
  }
  lastVisibleCheck = now;
  checkForUpdate();
};

export const registerAppUpdate = async () => {
  if (import.meta.env.DEV || !("serviceWorker" in navigator)) return;

  const hadController = Boolean(navigator.serviceWorker.controller);

  try {
    registration = await navigator.serviceWorker.register(SW_URL, {
      scope: "/",
    });
  } catch (error) {
    console.log("SW registration error", error);
    return;
  }

  // SW baru sudah menunggu sejak sebelum halaman dibuka (mis. user klik "Nanti Saja"
  // lalu reload biasa — SW waiting tidak aktif hanya karena reload)
  if (registration.waiting && hadController) {
    markUpdateAvailable();
  }
  watchInstallingSW(registration.installing);
  registration.addEventListener("updatefound", () => {
    watchInstallingSW(registration.installing);
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Instalasi pertama (clientsClaim), bukan versi baru
    if (!hadController) return;

    if (applying) {
      reloadPage();
      return;
    }
    // SW baru diaktifkan dari tab lain. Tab ini tidak di-reload paksa,
    // versi baru tetap dipasang saat user klik muat ulang / pindah halaman.
    markUpdateAvailable();
  });

  setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL);
  document.addEventListener("visibilitychange", checkOnVisible);
  window.addEventListener("focus", checkOnVisible);
};
